import React from "react";
import { useHistory } from "react-router-dom";
import { Typography, Button, Tooltip } from "@material-ui/core";
import { useQuestionnaire, usePublishCheck, usePublishQuestionnaire } from "./queries";
import { LOCALES } from "./api";
import { useLocaleDiffPreviews } from "./useLocaleDiffPreviews";
import type { SnapshotRef } from "./useLocaleDiffPreviews";
import DiffShell from "./components/DiffShell";
import RestoreDialog from "./components/RestoreDialog";
import type { Toast } from "./uiTypes";

const ReviewContent: React.VFC<{
  id: number;
  onPublishResult: (toast: Toast) => void;
}> = ({ id, onPublishResult }) => {
  const history = useHistory();

  const questionnaireQuery = useQuestionnaire(id);
  const publishCheckQuery = usePublishCheck(id);
  const publishMutation = usePublishQuestionnaire();
  const [restoreOpen, setRestoreOpen] = React.useState(false);

  const conflict = publishCheckQuery.data ?? null;
  const before: SnapshotRef | null = conflict ? { kind: "published", id: conflict.active_published_id } : null;
  const after: SnapshotRef | null = id ? { kind: "draft" } : null;

  const { previewsLoading, localeInfo } = useLocaleDiffPreviews({
    before,
    after,
    draftId: id || null,
    currentLocale: LOCALES[0],
  });

  const allLocalesLoaded = localeInfo.every(l => l.count !== null);
  const totalChanges = localeInfo.reduce((sum, l) => sum + (l.count ?? 0), 0);
  const noChanges = !!before && allLocalesLoaded && totalChanges === 0;

  const name = questionnaireQuery.data?.name ?? "...";

  const handlePublish = () => {
    publishMutation.mutate(id, {
      onSuccess: published => {
        onPublishResult({
          severity: "success",
          message: `Published ${name}`,
          to: `/health-forms/published/${published.id}`,
        });
        history.push("/health-forms/review");
      },
      onError: () => onPublishResult({ severity: "error", message: `Failed to publish ${name} — try again.` }),
    });
  };

  const handleOpenEditor = () => {
    history.push(`/health-forms/questionnaires/${id}`);
  };

  const subtitle = (
    <>
      {conflict && (
        <Typography variant="body2" color="textSecondary">
          Replacing version published {new Date(conflict.active_published_at).toLocaleString()} by{" "}
          {conflict.active_published_by}
        </Typography>
      )}
      {questionnaireQuery.data?.name && (
        <Typography variant="body2">
          <Button
            size="small"
            onClick={() =>
              history.push(`/health-forms/questionnaires/${encodeURIComponent(questionnaireQuery.data!.name!)}/compare`)
            }
          >
            Compare against other versions
          </Button>
        </Typography>
      )}
    </>
  );

  const actions = (
    <>
      <Button size="small" variant="outlined" onClick={handleOpenEditor}>
        Open editor
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setRestoreOpen(true)}
        disabled={!conflict || publishCheckQuery.isLoading || previewsLoading || noChanges}
      >
        Discard changes...
      </Button>
      <Tooltip title="Deploys to EU Stage and US Stage automatically. Production is deployed separately, per version, from the Published tab.">
        <span>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handlePublish}
            disabled={publishCheckQuery.isLoading || previewsLoading || publishMutation.isLoading || noChanges}
          >
            {publishMutation.isLoading ? "Publishing..." : "Publish"}
          </Button>
        </span>
      </Tooltip>
    </>
  );

  return (
    <>
      {conflict && (
        <RestoreDialog
          open={restoreOpen}
          onClose={() => setRestoreOpen(false)}
          draftId={id}
          publishedId={conflict.active_published_id}
          snapshotLabel="the published version"
        />
      )}
      <DiffShell
        embedded
        title={`Review: ${name}`}
        subtitle={subtitle}
        before={before}
        after={after}
        draftId={id || null}
        previousLabel="Currently published"
        nextLabel="About to publish"
        emptyMessage="No prior published version with this name. This will create the first snapshot."
        actions={actions}
        isLoading={publishCheckQuery.isLoading}
        error={publishCheckQuery.error ? "Failed to load publish check" : null}
      />
    </>
  );
};

export default ReviewContent;
